import { useEffect, useRef } from 'react'
import { useAppStore } from '../store/app'
import './InitiatorNotices.css'

// Passive "pending — X is handling" banner stack for actions the local user
// triggered that are gated on someone else's domain (X2, authz §2.6/§3.5).
// Informational only — never an approve/deny prompt (that's PermissionOverlay).
// A notice updates in place as the ladder escalates, then settles to a
// disposition and auto-clears a few seconds later. Mounted once in App.tsx.

// How long a settled (allowed/denied/expired) notice lingers before it clears.
const SETTLED_LINGER_MS = 6000

const ICON: Record<string, string> = {
  pending: '⏳',
  allowed: '✓',
  denied: '✕',
  expired: '⌛',
}

export function InitiatorNotices() {
  const notices = useAppStore((s) => s.initiatorNotices)
  const dismiss = useAppStore((s) => s.dismissInitiatorNotice)

  // One auto-dismiss timer per settled notice, scheduled exactly once. Keyed by
  // requestId so an unrelated store update (a new notice arriving) never clears
  // and fails to reschedule an in-flight timer — the bug of clearing every
  // timer on each render. Cleared only on unmount.
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map())

  useEffect(() => {
    for (const n of notices) {
      const existing = timers.current.get(n.requestId)
      if (n.status === 'pending') {
        // Reverted to pending (defensive) — cancel any dismiss timer.
        if (existing) {
          clearTimeout(existing)
          timers.current.delete(n.requestId)
        }
        continue
      }
      if (existing) continue // already counting down
      const handle = setTimeout(() => {
        timers.current.delete(n.requestId)
        dismiss(n.requestId)
      }, SETTLED_LINGER_MS)
      timers.current.set(n.requestId, handle)
    }
  }, [notices, dismiss])

  // Clear outstanding timers on unmount only.
  useEffect(() => {
    const map = timers.current
    return () => {
      for (const t of map.values()) clearTimeout(t)
    }
  }, [])

  if (notices.length === 0) return null
  return (
    <div className="initiator-notices">
      {notices.map((n) => (
        <div
          key={n.requestId}
          className={`initiator-notices__item initiator-notices__item--${n.status}`}
          role="status"
        >
          <span className="initiator-notices__icon" aria-hidden="true">
            {ICON[n.status] ?? '⏳'}
          </span>
          <span className="initiator-notices__body">{n.body}</span>
          {/* A pending notice reflects live state (and rehydrates from the pull
              surface on reconnect), so it isn't dismissible — it clears itself
              when the request resolves. Only settled notices get a dismiss. */}
          {n.status !== 'pending' && (
            <button
              className="initiator-notices__dismiss"
              aria-label="Dismiss"
              onClick={() => dismiss(n.requestId)}
            >
              ×
            </button>
          )}
        </div>
      ))}
    </div>
  )
}
